import {
  saveForLaterFailure,
  saveForLaterTiming,
  type SaveForLaterResult,
  type OnSaveForLater as SaveProductForLater,
} from '@contexts/ProductListsContext/saveForLater';
import type { LineItem, ProductProjection } from '@unity/commerce-bridge';
import type { OnSaveForLater } from '@unity/commerce-core';
import { resolveAssetStoreProductId } from './assetStoreProductId';
import type { FetchProductProjection } from './productProjections';

/** What the cart has loaded so far; read at call time, never captured. */
export type MiniCartSnapshot = {
  products: ProductProjection[] | undefined;
  lineItems: LineItem[];
  /** commerce-core's removal, as its provider last gave it. */
  removeLineItem?: (variables: { lineItemId: string }) => void;
};

export type SaveForLaterAdapterDeps = {
  getSnapshot: () => MiniCartSnapshot;
  fetchProduct: FetchProductProjection;
  /** Our lists-API save, keyed on the Asset Store product id. */
  saveForLater: SaveProductForLater;
  /**
   * After a confirmed save, before commerce-core removes the line item; one
   * `isLate`, after the shopper was told it failed, leaves the line in place.
   */
  onSaved?: (saved: {
    productId: string;
    assetStoreProductId: string;
    isLate: boolean;
  }) => void;
  /**
   * Any outcome but a save; commerce-core only logs it and keeps the line in
   * the cart, so this is the shopper's only signal.
   */
  onFailed?: (result: SaveForLaterResult) => void;
};

const loadProduct = async (
  productId: string,
  { getSnapshot, fetchProduct }: SaveForLaterAdapterDeps
) => {
  const loaded = getSnapshot().products?.find(({ id }) => id === productId);
  if (loaded) return loaded;
  try {
    return await fetchProduct(productId);
  } catch {
    // Not found, unpublished or offline: the product key may still map.
    return undefined;
  }
};

// The lists save times out on its own too, so a retry starts a new one.
export {
  SAVE_FOR_LATER_TIMEOUT_MS,
  saveForLaterTiming,
} from '@contexts/ProductListsContext/saveForLater';

const save = async (
  { productId }: Parameters<OnSaveForLater>[0],
  deps: SaveForLaterAdapterDeps,
  hasTimedOut: () => boolean
): Promise<SaveForLaterResult> => {
  // Optimistic line items carry no productId until the cart refetches.
  if (!productId) return saveForLaterFailure('UNKNOWN');

  const productKey = deps
    .getSnapshot()
    .lineItems.find((lineItem) => lineItem.productId === productId)?.productKey;
  const assetStoreProductId = resolveAssetStoreProductId(
    await loadProduct(productId, deps),
    productKey
  );
  if (!assetStoreProductId) return saveForLaterFailure('UNKNOWN');
  // The shopper was already told it failed, so no save may start now.
  if (hasTimedOut()) return saveForLaterFailure('NETWORK_ERROR');

  const saved = { productId, assetStoreProductId };
  const result = await deps.saveForLater({
    productId: assetStoreProductId,
    onSavedLate: () => deps.onSaved?.({ ...saved, isLate: true }),
  });
  if (result.saved) deps.onSaved?.({ ...saved, isLate: hasTimedOut() });
  return result;
};

/**
 * commerce-ui calls `onSaveForLater` with the line item's commercetools
 * product id; our lists API keys on the Asset Store product id. This maps one
 * to the other first and fails with `UNKNOWN` when it cannot, so a
 * commercetools id never reaches the lists API (it would "save" an item that
 * then renders broken). commerce-core seeds the callback once, so the adapter
 * holds no state of its own and reads the cart through `getSnapshot`.
 */
export const createSaveForLaterAdapter =
  (deps: SaveForLaterAdapterDeps): OnSaveForLater =>
  async (params) => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let hasTimedOut = false;
    const timeout = new Promise<SaveForLaterResult>((resolve) => {
      timer = setTimeout(() => {
        hasTimedOut = true;
        resolve(saveForLaterFailure('NETWORK_ERROR'));
      }, saveForLaterTiming.timeoutMs);
    });
    const result = await Promise.race([
      save(params, deps, () => hasTimedOut).catch(() =>
        saveForLaterFailure('UNKNOWN')
      ),
      timeout,
    ]).finally(() => clearTimeout(timer));
    if (!result.saved) deps.onFailed?.(result);
    return result;
  };
